import { useEffect, useState } from "react";
import { useLocation } from "react-router-dom";
import { AlertTriangle, CalendarDays, Check, CheckCircle2, Loader2, MapPin, ShieldCheck } from "lucide-react";
import api from "@/lib/api";
import { useSEO } from "@/lib/useSEO";
import { THC } from "@/lib/tankHealthCheck";
import { TESTS } from "@/lib/siteData";
import { CampaignHeader } from "@/components/tank-health-check/CampaignHeader";
import { CampaignFooter } from "@/components/tank-health-check/CampaignFooter";

const money = (cents) =>
  `$${(Number(cents || 0) / 100).toFixed(2).replace(/\.00$/, "")}`;

/** Booking-aware 20% lab offer: live packages, server-priced discount and
    a one-click Stripe checkout that reuses the Health Check booking. */
export default function TankWaterHealthCheckLabOffer() {
  const token = new URLSearchParams(useLocation().search).get("token") || "";
  const [data, setData] = useState(null);
  const [state, setState] = useState("loading");
  const [busy, setBusy] = useState("");
  const [error, setError] = useState("");
  const [bypassed, setBypassed] = useState(false);

  useSEO({ title: "Your 20% AWARE lab offer", noindex: true, includeBaseline: false });

  useEffect(() => {
    if (!token) { setState("invalid"); return; }
    api.get("/tank-water-health-check/lab-offer", { params: { token } })
      .then(({ data: d }) => { setData(d); setState("ok"); })
      .catch(() => setState("invalid"));
  }, [token]);

  const offer = data?.offer;
  const ordered = Boolean(data?.already_ordered);
  const testMode = Boolean(data?.test_mode);

  const checkout = async (testId) => {
    // Test-mode ONLY (controlled verification bookings): payment is
    // intentionally bypassed, so the browser never opens Stripe checkout and
    // never shows the checkout failure message. Genuine customer bookings are
    // untouched — they still go to Stripe and still surface real failures.
    if (testMode) { setBypassed(true); setError(""); return; }
    setBusy(testId); setError("");
    try {
      const { data: res } = await api.post("/tank-water-health-check/lab-offer/checkout", {
        offer_token: token, test_id: testId, origin_url: window.location.origin,
      });
      if (res.checkout_url) { window.location.href = res.checkout_url; return; }
      if (res.already_ordered) { window.location.reload(); return; }
      setError("We couldn’t open secure checkout. Please try again.");
    } catch (e) {
      const status = e?.response?.status;
      if (status === 409) { window.location.reload(); return; }
      setError(status === 422
        ? "We need to confirm a couple of contact details before checkout — please call (07) 5327 3473."
        : "We couldn’t open secure checkout. Please try again.");
    } finally {
      setBusy("");
    }
  };

  return (
    <div className="min-h-screen bg-white" data-testid="thc-lab-offer-page">
      <CampaignHeader onBook={() => { window.location.href = "/tank-water-health-check#book"; }} />
      <main className="mx-auto w-full max-w-[1440px] px-[18px] md:px-8 py-10 md:py-14">
        {state === "loading" && (
          <p className="flex items-center gap-2 text-base" style={{ color: THC.muted }}>
            <Loader2 className="w-5 h-5 animate-spin" aria-hidden="true" /> Checking your offer…
          </p>
        )}

        {state === "invalid" && (
          <div className="max-w-[720px] rounded-[14px] border p-6" data-testid="thc-offer-invalid"
            style={{ borderColor: THC.border, backgroundColor: THC.faint }}>
            <h1 className="font-extrabold uppercase text-[26px] md:text-[32px] leading-[1.05]"
              style={{ color: THC.navy }}>
              THIS OFFER LINK IS NO LONGER AVAILABLE
            </h1>
            <p className="mt-2 text-base" style={{ color: THC.body }}>
              If you believe this is an error, please contact AWARE Water Testing.
            </p>
            <a href="/tank-water-health-check" data-testid="thc-offer-invalid-back"
              className="mt-4 inline-block rounded-full px-5 py-3 font-extrabold uppercase text-sm text-white
                         transition-transform hover:-translate-y-0.5"
              style={{ backgroundColor: THC.blue }}>
              BACK TO THE HEALTH CHECK PAGE
            </a>
          </div>
        )}

        {state === "ok" && offer && (
          <div className="max-w-[980px]">
            <h1 className="font-extrabold uppercase text-[29px] md:text-[40px] leading-[1.03]"
              style={{ color: THC.navy }} data-testid="thc-offer-heading">
              {ordered ? "YOUR LAB TEST IS ADDED" : `YOUR ${offer.percent}% AWARE LAB OFFER`}
            </h1>
            <p className="mt-3 text-base md:text-lg leading-[1.5] max-w-[760px]" style={{ color: THC.body }}>
              {ordered
                ? "Your AWARE laboratory test has been added to your Tank Water Health Check. The attending Pristine Water Systems technician will collect the sample during your visit."
                : "Your Tank Water Health Check is booked. Add an AWARE laboratory water test now and the attending Pristine Water Systems technician can collect your sample during the visit."}
            </p>

            <div className="mt-6 grid sm:grid-cols-3 gap-3" data-testid="thc-offer-visit">
              <Fact icon={MapPin} label="PROPERTY" value={offer.property_address} testId="thc-offer-address" />
              <Fact icon={CalendarDays} label="HEALTH CHECK" value={offer.date_display} testId="thc-offer-date" />
              <Fact icon={CalendarDays} label="ARRIVAL WINDOW" value={offer.arrival_window_display} testId="thc-offer-time" />
            </div>

            {ordered && (
              <div className="mt-6 rounded-[14px] border p-5 md:p-6" data-testid="thc-offer-already-ordered"
                style={{ borderColor: THC.border, backgroundColor: THC.faint }}>
                <div className="flex items-start gap-3">
                  <CheckCircle2 className="w-6 h-6 shrink-0" style={{ color: THC.blue }} aria-hidden="true" />
                  <div>
                    <h2 className="font-extrabold uppercase text-base md:text-lg leading-tight"
                      style={{ color: THC.navy }}>
                      YOU’VE ALREADY ORDERED {(data.lab_order?.test_name || "YOUR AWARE TEST").toUpperCase()}
                    </h2>
                    <p className="mt-1 text-sm font-bold" style={{ color: THC.blue }}
                      data-testid="thc-offer-order-reference">
                      Reference: {data.lab_order?.reference}
                    </p>
                    <p className="mt-2 text-base" style={{ color: THC.body }}>
                      Your AWARE laboratory test has already been added to your Tank Water Health
                      Check. The attending Pristine Water Systems technician will collect your
                      sample during the visit. Nothing else to do.
                    </p>
                    <p className="mt-2 text-sm" style={{ color: THC.muted }}>
                      Need an additional test? Book it through the standard AWARE booking flow.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {!ordered && (
              <>
                <div className="mt-7 text-[13px] font-bold uppercase tracking-wide"
                  style={{ color: THC.blue }} data-testid="thc-offer-discount-strip">
                  {offer.percent}% CAMPAIGN DISCOUNT APPLIED — NO DISCOUNT CODE REQUIRED
                </div>

                <div className="mt-3 grid gap-4 lg:grid-cols-3" data-testid="thc-offer-packages">
                  {(data.packages || []).map((p) => (
                    <PackageCard key={p.test_id} pkg={p} busy={busy}
                      onChoose={() => checkout(p.test_id)} />
                  ))}
                </div>

                {bypassed && (
                  <div className="mt-4 rounded-[12px] border-2 p-4"
                    style={{ borderColor: THC.blue, backgroundColor: THC.light }}
                    data-testid="thc-offer-test-mode-bypass" role="status">
                    <p className="text-[13px] font-extrabold uppercase tracking-wide"
                      style={{ color: THC.navy }}>
                      TEST MODE — PAYMENT BYPASSED
                    </p>
                    <p className="mt-1 text-sm" style={{ color: THC.body }}>
                      No payment has been taken. Continue with the controlled production
                      verification.
                    </p>
                  </div>
                )}

                {error && (
                  <p className="mt-4 text-sm font-semibold" style={{ color: THC.red }}
                    data-testid="thc-offer-error">{error}</p>
                )}

                <div className="mt-5 flex items-start gap-2 text-sm rounded-[12px] border p-4"
                  style={{ borderColor: THC.border, backgroundColor: THC.faint, color: THC.body }}
                  data-testid="thc-offer-trust-line">
                  <ShieldCheck className="w-4 h-4 mt-0.5 shrink-0" style={{ color: THC.blue }} aria-hidden="true" />
                  <span>
                    No extra visit required — your sample can be collected during your scheduled
                    Tank Water Health Check.
                  </span>
                </div>
              </>
            )}

            <div className="mt-5 flex items-start gap-2 text-sm" style={{ color: THC.muted }}>
              <AlertTriangle className="w-4 h-4 mt-0.5 shrink-0" aria-hidden="true" />
              <span>
                Reference {offer.reference} — the owner must be present at the Health Check,
                which is conducted by Pristine Water Systems. Laboratory analysis and
                reporting are carried out by AWARE Water Testing.
              </span>
            </div>
          </div>
        )}
      </main>
      <CampaignFooter />
    </div>
  );
}

function PackageCard({ pkg, busy, onChoose }) {
  const meta = TESTS.find((t) => t.id === pkg.test_id) || {};
  const loading = busy === pkg.test_id;
  return (
    <div className="rounded-[16px] border p-5 flex flex-col transition-shadow hover:shadow-lg"
      data-testid={`thc-package-${pkg.test_id}`}
      style={{
        borderColor: pkg.recommended ? THC.blue : THC.border,
        borderWidth: pkg.recommended ? 2 : 1,
        backgroundColor: pkg.recommended ? THC.light : "#FFFFFF",
        boxShadow: THC.shadow,
      }}>
      {pkg.recommended && (
        <span className="self-start rounded-full px-3 py-1 text-[11px] font-bold uppercase tracking-wide text-white"
          style={{ backgroundColor: THC.blue }} data-testid="thc-package-recommended">
          RECOMMENDED
        </span>
      )}
      <h3 className="mt-2 font-extrabold uppercase text-base md:text-lg leading-tight"
        style={{ color: THC.navy }}>{pkg.name}</h3>

      <ul className="mt-3 space-y-1.5">
        {(meta.highlights || []).slice(0, 5).map((h) => (
          <li key={h} className="flex items-start gap-2 text-sm" style={{ color: THC.body }}>
            <Check className="w-4 h-4 mt-0.5 shrink-0" style={{ color: THC.blue }} aria-hidden="true" /> {h}
          </li>
        ))}
      </ul>

      <div className="mt-4 pt-4 border-t mt-auto" style={{ borderColor: THC.border }}>
        <div className="text-sm" style={{ color: THC.muted }}>
          Standard price:{" "}
          <span className="line-through" data-testid={`thc-package-standard-${pkg.test_id}`}>
            {money(pkg.standard_price_cents)}
          </span>
        </div>
        <div className="mt-1 font-extrabold text-[28px] leading-none" style={{ color: THC.navy }}
          data-testid={`thc-package-price-${pkg.test_id}`}>
          {money(pkg.final_price_cents)}
        </div>
        <div className="mt-1 text-sm font-bold" style={{ color: THC.blue }}
          data-testid={`thc-package-saving-${pkg.test_id}`}>
          You save {money(pkg.discount_amount_cents)} ({pkg.discount_percent}% off)
        </div>
      </div>

      <button type="button" onClick={onChoose} disabled={Boolean(busy)}
        data-testid={`thc-package-cta-${pkg.test_id}`}
        className="mt-4 w-full rounded-full px-5 py-3 font-extrabold uppercase text-sm text-white
                   transition-transform hover:-translate-y-0.5 disabled:opacity-60 disabled:translate-y-0"
        style={{ backgroundColor: pkg.recommended ? THC.red : THC.blue }}>
        {loading ? "OPENING SECURE CHECKOUT…" : `GET MY ${pkg.discount_percent}% OFF`}
      </button>
    </div>
  );
}

function Fact({ icon: Icon, label, value, testId }) {
  return (
    <div className="rounded-[12px] border p-4" style={{ borderColor: THC.border }} data-testid={testId}>
      <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wide"
        style={{ color: THC.muted }}>
        <Icon className="w-4 h-4" style={{ color: THC.blue }} aria-hidden="true" /> {label}
      </div>
      <div className="mt-1 text-base font-bold leading-snug" style={{ color: THC.navy }}>
        {value || "—"}
      </div>
    </div>
  );
}
